import { useCallback, useEffect, useState } from 'react';
import {
  AuthActions,
  useAuthDispatch,
  useAuthSelector,
} from '@wajeez-web/stores/auth';
import { addInitializer } from '@wajeez-web/core/types';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRouting } from '../other/initialization';
import { initializePlans } from '../other/initialization/initializePlans';
import { initializeFlowReader } from '../other/initialization/initializeFlowReader';
import {
  useContentSelector,
  useContentDispatch,
  InterestsActions,
} from '@wajeez-web/stores/content';
import { Server } from '@wajeez-web/core/api-base';
import useQuery from '../hooks/useQuery';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@wajeez-web/components/ui';
import { getQueryFromUrl, updateQueryParam } from '@wajeez-web/core/utils';

export function AuthorizedRoute(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useContentDispatch();
  const authDispatch = useAuthDispatch();

  const isAuthorized = useAuthSelector((s) => !!s.auth.token);
  const initUrl = useAuthSelector(({ auth }) => auth.initURL);
  const isB2b = getQueryFromUrl('b2b') === '1';

  useRouting(
    [
      addInitializer('initializePlans', 0, initializePlans),
      addInitializer('initializeFlowReader', 0, initializeFlowReader),
    ],
    isAuthorized
  );

  useEffect(() => {
    const userInterests = JSON.parse(
      localStorage.getItem('userInterests') || '[]'
    );

    dispatch(InterestsActions.setUserInterests(userInterests));
  }, [dispatch]);

  if (!isAuthorized) {
    !initUrl && authDispatch(AuthActions.initUrl(location.pathname));

    const currentUrl = location.pathname + location.search;

    if (isB2b) {
      return (
        <Navigate
          to={`/login/b2b${location.search
            ? `?redirectUrl=${encodeURIComponent(currentUrl)}`
            : ''
            }`}
          state={{ from: location }}
          replace
        />
      );
    }
    return (
      <Navigate
        to={`/login${location.search
          ? `?redirectUrl=${encodeURIComponent(currentUrl)}`
          : ''
          }`}
        state={{ from: location }}
        replace
      />
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={(details) => {
        navigate('/');
      }}
    >
      <Outlet />
    </ErrorBoundary>
  );
}

export const OnlyPublicRoute = () => {
  const location = useLocation();

  const isAuthorized = useAuthSelector((s) => !!s.auth.token);
  const initUrl =
    useAuthSelector(({ auth }) => auth.initURL) || location.pathname;

  const permission = useAuthSelector(({ auth }) => auth.permission);

  const endpoints = useCommonSelector(({ navigation }) => navigation.endpoints);

  const isAuthorizedRoute = endpoints
    .find((e) => '/' + e.endpoint === initUrl)
    ?.per.find((p) => permission?.includes(p));

  let from = location.state?.from?.pathname || '/';
  from = isAuthorizedRoute ? from : '/';

  const redirectUrl = getQueryFromUrl('redirectUrl')
    ? decodeURIComponent(getQueryFromUrl('redirectUrl') ?? '')
    : undefined;

  if (isAuthorized) {
    return <Navigate to={redirectUrl || from} replace />;
  }

  return <Outlet />;
};

export const OnlyPublicRouteCoordination = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthorized = useAuthSelector((s) => !!s.auth.token);
  const initUrl =
    useAuthSelector(({ auth }) => auth.initURL) || location.pathname;

  const permission = useAuthSelector(({ auth }) => auth.permission);

  const endpoints = useCommonSelector(({ navigation }) => navigation.endpoints);

  const isAuthorizedRoute = endpoints
    .find((e) => '/' + e.endpoint === initUrl)
    ?.per.find((p) => permission?.includes(p));

  if (isAuthorized) {
    const from = isAuthorizedRoute
      ? initUrl || '/coordination-content'
      : '/coordination-content';

    return <Navigate to={from} replace />;
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={(details) => {
        navigate('/');
      }}
    >
      <Outlet />;
    </ErrorBoundary>
  );
};

export interface SearchParamsMain {
  email: string | null | undefined;
  oauthToken: string | null | undefined;
  oauthVerifier: string | null | undefined;
  code: string | null | undefined;
  create: string | null | undefined;
  token: string | null | undefined;
}

export const OnlyPublicRouteWithoutUserInterests = () => {
  const contentDispatch = useContentDispatch();
  const location = useLocation();

  const query = useQuery();

  const [searchParams] = useState<SearchParamsMain>({
    email: query.get('email') ? query.get('email') : '',
    oauthToken: query.get('oauth_token') ? query.get('oauth_token') : '',
    oauthVerifier: query.get('oauth_verifier')
      ? query.get('oauth_verifier')
      : '',
    code: query.get('code') || '',
    create: query.get('create') || '',
    token: query.get('token') || '',
  });
  const [allowInterests, setAllowInterests] = useState(false);

  const isAuthorized = useAuthSelector((s) => !!s.auth.token);
  const initUrl = useAuthSelector(({ auth }) => auth.initURL);

  const userInterests = useContentSelector(
    ({ interests }) => interests.userInterests
  );

  const from = initUrl || '/home';
  const redirectUrl = getQueryFromUrl('redirectUrl')
    ? decodeURIComponent(getQueryFromUrl('redirectUrl') ?? '')
    : undefined;

  const handleUserInterests = useCallback(async () => {
    let userInterests = JSON.parse(
      localStorage.getItem('userInterests') || '[]'
    );

    if (!userInterests.length) {
      const res = await contentDispatch(
        InterestsActions.getUserInterestsAsync()
      );

      userInterests = res.payload as Server.InterestOutput[];

      localStorage.setItem('userInterests', JSON.stringify(userInterests));
    }

    setAllowInterests(true);
  }, [contentDispatch]);

  useEffect(() => {
    if (isAuthorized) {
      handleUserInterests();
    }
  }, [isAuthorized, handleUserInterests]);

  useEffect(() => {
    const { email, oauthToken, oauthVerifier, code, create, token } =
      searchParams;

    updateQueryParam('email', email);
    updateQueryParam('oauthToken', oauthToken);
    updateQueryParam('oauthVerifier', oauthVerifier);
    updateQueryParam('code', code);
    updateQueryParam('create', create);
    updateQueryParam('token', token);
  }, [searchParams]);

  console.log(isAuthorized, redirectUrl, from,allowInterests)
  if (isAuthorized && userInterests?.length && allowInterests) {
    return <Navigate to={redirectUrl ?? from ?? "/"} replace />;
  }

  if (
    isAuthorized &&
    userInterests &&
    userInterests?.length === 0 &&
    allowInterests
  ) {
    const currentUrl = location.pathname + location.search;

    return (
      <Navigate
        to={`/interests${currentUrl ? `?redirectUrl=${encodeURIComponent(currentUrl)}` : ''
          }`}
        replace
      />
    );
  }

  if (isAuthorized) {
    return <Navigate to={redirectUrl ?? from ?? "/"} replace />;
  }

  return <Outlet />;
};
